import React from "react";
import TextInput from "components/TextInput";
import { getCurrentReferenceUnitValue } from "features/MappingEditor/utils";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { ComponentDetailsByFileOut } from "api-client";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  IconArrowDown,
  IconArrowUp,
  IconGripVertical,
} from "@tabler/icons-react";
import clsx from "clsx";
import ComponentChildContent from "../components/ComponentChildContent";
import { COEStore } from "../ComponentEditorStore";
import TextArea from "components/TextArea";
import Tooltip from "components/Tooltip";

export interface ChildProps extends ComponentDetailsByFileOut {
  user_lifespan?: number;
}

const AddedChild = ({
  childItem,
  rowIndex,
  updateThickness,
  ...props
}: {
  childItem: ChildProps;
  rowIndex: number;
  moveUp: (index: number) => void;
  moveDown: (index: number) => void;
  sortableId?: string;
  referenceUnitChanged?: () => void;
  updateThickness?: boolean;
  setUpdateThickness?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const itemReferenceUnit = getCurrentReferenceUnitValue(
    childItem.reference_unit
  );
  const componentRefUnitID = COEStore?.data.reference_unit.id;

  const getThicknessCondition = () => {
    return itemReferenceUnit?.id === "M2" ? "and" : "or";
  };

  const hasAnyComponentChild = () => {
    return COEStore.data.childrenList.some((rec) => rec.type === "COMPONENT");
  };

  React.useEffect(() => {
    /*  Change thikness on change of reference unit to M3 */

    if (
      getThicknessCondition() === "or" &&
      childItem.unit_conversion &&
      updateThickness
    ) {
      onChangeValue("thickness", String(+childItem.unit_conversion * 1000));
    }
    props.setUpdateThickness?.(false);
  }, [updateThickness]);

  const onChangeValue = (key: keyof ChildProps, value?: string | number) => {
    if (key === "unit_conversion") {
      props.referenceUnitChanged?.();
    }
    COEStore.onChangeChildParams(key, rowIndex, value);
  };

  const handleChangeFactor = (value: string) => {
    onChangeValue("unit_conversion", value);
    if (getThicknessCondition() === "or") {
      const thickness = Number(value) * 1000;
      onChangeValue("thickness", thickness || "");
    }
  };

  const handleChangeThickness = async (value: string) => {
    onChangeValue("thickness", value);
    if (getThicknessCondition() === "or") {
      const unitCoversion = Number(value) / 1000;
      onChangeValue("unit_conversion", unitCoversion || "");
    }
  };

  React.useEffect(() => {
    (async () => await COEStore.fetchComponentInsights())();
  }, [childItem.thickness]);

  const id = String(props.sortableId);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 10 : "auto",
  };

  const isFirst = rowIndex === 0;
  const isLast = rowIndex === COEStore.data.childrenList.length - 1;

  const reorderOptions = (
    <div className="flex items-center transition">
      <IconArrowUp
        className={clsx(
          "text-indigo-700 hover:text-indigo-500 w-6 h-6 mr-1 cursor-pointer",
          {
            "cursor-not-allowed pointer-events-none opacity-50": isFirst,
          }
        )}
        onClick={isFirst ? undefined : () => props.moveUp(rowIndex)}
      />
      <IconArrowDown
        className={clsx(
          "text-indigo-700 hover:text-indigo-500 w-6 h-6 mr-1 cursor-pointer",
          {
            "cursor-not-allowed pointer-events-none opacity-50": isLast,
          }
        )}
        onClick={isLast ? undefined : () => props.moveDown(rowIndex)}
      />
    </div>
  );

  const dragHandle = (
    <div
      className="flex items-center bg-gray-50 justify-center w-10 border-r border-gray-200 cursor-grab"
      {...listeners}
      {...attributes}
    >
      <IconGripVertical
        size={24}
        className={clsx(
          "min-w-[24px]focus:outline-none text-indigo-700 hover:text-indigo-500 cursor-grab"
        )}
      />
    </div>
  );

  function isThicknessVisible() {
    return (
      componentRefUnitID === "M2" &&
      ["M2", "M3"].includes(childItem.reference_unit) &&
      !hasAnyComponentChild()
    );
  }

  return (
    <div
      className="w-full bg-white border border-gray-300 rounded-md overflow-hidden mb-3 flex"
      style={style}
      ref={setNodeRef}
    >
      {dragHandle}
      <div className="w-full">
        <ComponentChildContent
          childItem={childItem}
          removable
          reorderOptions={reorderOptions}
          className="relative"
          hideInfoIcon
          moveUp={rowIndex === 0 ? undefined : () => props.moveUp(rowIndex)}
          moveDown={
            rowIndex === COEStore.data.childrenList.length - 1
              ? undefined
              : () => props.moveDown(rowIndex)
          }
          order={rowIndex + 1}
          showOrder
        />
        <div className="bg-gray-50 border-t border-gray-200 p-3 gap-4 min-w-[200px]">
          <div className="flex mb-2 items-center">
            <TextInput
              unit={`${
                isEng
                  ? itemReferenceUnit?.name ?? "-"
                  : itemReferenceUnit?.name_de ?? "-"
              }/${COEStore.getValueFromObject("reference_unit", isEng)}`}
              noMargin
              handleChange={handleChangeFactor}
              placeholder={t("components.specifyFactor")}
              value={childItem.unit_conversion as unknown as string}
              type="number"
              min={0}
              hasError={
                Number(childItem.unit_conversion) === 0 ||
                childItem.unit_conversion === undefined
              }
              className="border-gray-200"
              containerClass="w-full"
            />
            {isThicknessVisible() && (
              <div className="flex items-center w-full">
                <div className="mx-2 text-gray-400 text-xs">
                  {t(`commons.${getThicknessCondition()}`)}
                </div>
                <TextInput
                  unit="mm"
                  noMargin
                  handleChange={handleChangeThickness}
                  placeholder={t("components.ticknessPlaceholder")}
                  value={childItem.thickness ?? ""}
                  type="number"
                  min={0}
                  containerClass="w-full"
                  className="border-gray-200"
                />
                {componentRefUnitID === "M2" &&
                  childItem.reference_unit === "M2" && (
                    <Tooltip
                      content={t("components.ticknessTooltip")}
                      className="text-gray-500"
                    />
                  )}
              </div>
            )}
          </div>

          {childItem.type === "PRODUCT" && (
            <div className="flex w-full items-center mb-2">
              <TextInput
                unit={t("components.year")}
                noMargin
                handleChange={(value) => onChangeValue("user_lifespan", value)}
                placeholder={t("components.lifeSpanPlaceholder", {
                  value: childItem.life_span ?? "-",
                })}
                value={
                  childItem.life_span_overwritten
                    ? childItem.user_lifespan
                    : undefined
                }
                type="number"
                min={0}
                containerClass="w-full"
                className="border-gray-200"
              />
              <Tooltip
                content={t("components.lifeSpanTooltip")}
                className=" text-gray-500"
              />
            </div>
          )}

          <TextArea
            placeholder={t("components.addComment")}
            containerClass="w-full"
            handleChange={(value) => onChangeValue("comment", value)}
            value={childItem.comment ?? ""}
            className="border-gray-200"
          />
        </div>
      </div>
    </div>
  );
};

export default observer(AddedChild);
